import { isEscape, returnFocus, trapFocus } from "./utils.js";

class CartPanel extends HTMLElement {
    connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.icon = document.getElementById('cart-icon');
        this.abortController = null;
        this._onClickOutside = this.onClickOutside.bind(this);

        // open panel icon
        this.icon.addEventListener('click', e => {
            e.preventDefault();
            window.returnFocusTo = this.icon;
            this.open();
        });

        // escape support
        document.body.addEventListener('keyup', e => {
            if (isEscape(e)) {
                this.close();
            }
        });

        // update cart when quantity input changes
        this.addEventListener('quantityChanged', e => {
            let updates = {};
            updates[e.detail.key] = e.detail.quantity;
            this.updateCart(updates);
        });

        // delegated click handler for close button and remove links
        this.addEventListener('click', e => {
            if (e.target.closest('.cart-panel__close')) {
                e.preventDefault();
                this.close();
                return;
            }
            const removeBtn = e.target.closest('.cart-panel__remove');
            if (removeBtn) {
                e.preventDefault();
                const url = new URL(removeBtn.href);
                let updates = {};
                updates[url.searchParams.get('id')] = 0;
                this.updateCart(updates);
            }
        });

        /*
        Open the cart when items are added
        */
        document.addEventListener('cartUpdated', () => {
            this.open();
        });
    }

    open() {
        this.removeAttribute('inert');
        document.body.classList.add('cart-panel-open');
        this._releaseFocusTrap = trapFocus(this);
        document.addEventListener('click', this._onClickOutside);
    }

    close() {
        document.body.classList.remove('cart-panel-open');
        document.removeEventListener('click', this._onClickOutside);
        this._releaseFocusTrap?.();
        this.setAttribute('inert', '');
        returnFocus();
    }

    onClickOutside(e) {
        if (!this.contains(e.target) && !this.icon.contains(e.target)) {
            this.close();
        }
    }

    async updateCart(updates) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        document.getElementById('proceed-to-checkout').textContent = "Updating...";
        try {
            const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates }),
                signal: this.abortController.signal
            });
            if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
            await response.json();
            await this.getSections();
        } catch (error) {
            if (error.name !== 'AbortError') console.error('Error:', error);
        }
    }

    // call to Shopify's section rendering API: https://shopify.dev/docs/api/ajax/section-rendering
    async getSections() {
        try {
            const response = await fetch(window.Shopify.routes.root + 'cart/?sections=cart-panel,header');
            if (!response.ok) {
                throw new Error(`Response status: ${response.status}`);
            }

            const result = await response.json();
            this.updateElements(result);
        } catch (error) {
            console.error(error.message);
        }
    }

    // update page elements after getting the new section content
    updateElements(result) {
        const parser = new DOMParser();
        let section = parser.parseFromString(result['cart-panel'], 'text/html');
        const newCart = section.body.firstChild.querySelector('#cart-panel');
        section = parser.parseFromString(result.header, 'text/html');
        const newCartIcon = section.body.firstChild.querySelector('#cart-icon');

        this.innerHTML = newCart.innerHTML;
        this.icon.innerHTML = newCartIcon.innerHTML;
    }
}

customElements.define('cart-panel', CartPanel);
